import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import papi, { perr, PTOKEN } from "@/lib/portalApi";
import { pp } from "@/lib/portalHost";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, ShieldCheck } from "lucide-react";

export function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen bg-[#f4f7f5] flex flex-col lg:flex-row">
      <div className="relative lg:w-[46%] bg-[#076633] text-white px-8 py-10 lg:p-14 flex flex-col justify-between overflow-hidden">
        <div className="absolute -right-24 -top-24 h-80 w-80 rounded-full bg-white/5" /><div className="absolute -left-10 bottom-0 h-56 w-56 rounded-full bg-white/5" />
        <img src="/logo-omnibat.png" alt="OMNIBAT" className="h-12 w-auto self-start bg-white rounded-xl p-2 relative" />
        <div className="relative mt-8 lg:mt-0">
          <p className="uppercase tracking-[0.25em] text-xs text-emerald-200">Espace client</p>
          <h1 className="font-head text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mt-3">Votre projet OMNIBAT, directement dans votre poche.</h1>
          <p className="mt-4 text-emerald-100 max-w-md">Suivez votre projet de construction où que vous soyez : avancement, photos du chantier, documents et paiements.</p>
        </div>
        <p className="relative hidden lg:flex items-center gap-2 text-xs text-emerald-200"><ShieldCheck className="h-4 w-4" />Accès personnel et sécurisé</p>
      </div>
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm">
          <h2 className="font-head text-2xl font-bold text-slate-900">{title}</h2>
          {subtitle && <p className="text-sm text-slate-500 mt-1 mb-6">{subtitle}</p>}
          {children}
        </div>
      </div>
    </div>
  );
}

function Err({ msg }) { return msg ? <p className="text-sm text-rose-600 bg-rose-50 rounded-lg px-3 py-2" data-testid="portal-auth-error">{msg}</p> : null; }

export function PortalLogin() {
  const nav = useNavigate();
  const [f, setF] = useState({ identifier: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      if (forgot) { await papi.post("/auth/forgot", { identifier: f.identifier, origin: window.location.origin + pp("").replace(/\/$/, "") }); setSent(true); }
      else { const r = await papi.post("/auth/login", f); localStorage.setItem(PTOKEN, r.data.token); nav(pp()); }
    } catch (x) { setErr(perr(x)); } finally { setBusy(false); }
  };
  return (
    <AuthShell title={forgot ? "Mot de passe oublié" : "Bienvenue dans votre espace client"} subtitle={forgot ? "Saisissez votre e-mail : vous recevrez un lien de réinitialisation." : "Suivez votre projet de construction où que vous soyez."}>
      <form onSubmit={submit} className="space-y-3" data-testid="portal-login-form">
        <Input placeholder="Téléphone ou e-mail" value={f.identifier} onChange={(e) => setF({ ...f, identifier: e.target.value })} className="h-12" data-testid="portal-login-identifier" />
        {!forgot && <Input type="password" placeholder="Mot de passe" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="h-12" data-testid="portal-login-password" />}
        <Err msg={err} />
        {sent && <p className="text-sm text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2" data-testid="portal-forgot-sent">Si un compte correspond, un e-mail vient de vous être envoyé.</p>}
        <Button disabled={busy || !f.identifier} className="w-full h-12 bg-[#076633] hover:bg-[#05522a] text-white rounded-xl text-base" data-testid="portal-login-submit">
          {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{forgot ? "Envoyer le lien" : "Se connecter"}</Button>
        <button type="button" onClick={() => { setForgot(!forgot); setErr(""); setSent(false); }} className="w-full text-sm text-[#076633] hover:underline" data-testid="portal-forgot-toggle">{forgot ? "Retour à la connexion" : "Mot de passe oublié ?"}</button>
        <p className="text-xs text-slate-400 text-center pt-2">Connexion par code SMS : bientôt disponible</p>
      </form>
    </AuthShell>
  );
}

function PwForm({ onSubmit, label, busy, err }) {
  const [a, setA] = useState(""); const [b, setB] = useState("");
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(a); }} className="space-y-3">
      <Input type="password" placeholder="Nouveau mot de passe (8 caractères, lettres et chiffres)" value={a} onChange={(e) => setA(e.target.value)} className="h-12" data-testid="portal-pw-new" />
      <Input type="password" placeholder="Confirmer le mot de passe" value={b} onChange={(e) => setB(e.target.value)} className="h-12" data-testid="portal-pw-confirm" />
      {a && b && a !== b && <p className="text-xs text-rose-600">Les mots de passe ne correspondent pas.</p>}
      <Err msg={err} />
      <Button disabled={busy || !a || a !== b} className="w-full h-12 bg-[#076633] hover:bg-[#05522a] text-white rounded-xl" data-testid="portal-pw-submit">{label}</Button>
    </form>
  );
}

export function PortalAccess() {
  const { token } = useParams();
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { papi.get(`/auth/access/${token}`).then((r) => setInfo(r.data)).catch((e) => setInfo({ error: perr(e) })); }, [token]);
  const activate = async (password) => {
    setBusy(true); setErr("");
    try { await papi.post("/auth/activate", { token, password }); nav(pp("/login?activated=1")); } catch (e) { setErr(perr(e)); } finally { setBusy(false); }
  };
  if (!info) return <AuthShell title="Chargement…"><Loader2 className="h-6 w-6 animate-spin text-[#076633]" /></AuthShell>;
  if (info.error) return <AuthShell title="Lien indisponible" subtitle={info.error}><Link to={pp("/login")} className="text-[#076633] underline">Aller à la connexion</Link></AuthShell>;
  if (info.activated) return <AuthShell title={`Bonjour ${info.client_name}`} subtitle="Votre accès est déjà activé."><Button onClick={() => nav(pp("/login"))} className="w-full h-12 bg-[#076633] text-white rounded-xl" data-testid="portal-access-login">Se connecter</Button></AuthShell>;
  return <AuthShell title={`Bienvenue ${info.client_name}`} subtitle="Choisissez votre mot de passe pour activer votre espace client."><PwForm onSubmit={activate} label="Activer mon espace" busy={busy} err={err} /></AuthShell>;
}

export function PortalReset() {
  const { token } = useParams();
  const nav = useNavigate();
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const go = async (password) => { setBusy(true); try { await papi.post("/auth/reset", { token, password }); nav(pp("/login")); } catch (e) { setErr(perr(e)); } finally { setBusy(false); } };
  return <AuthShell title="Nouveau mot de passe" subtitle="Choisissez un nouveau mot de passe."><PwForm onSubmit={go} label="Enregistrer" busy={busy} err={err} /></AuthShell>;
}
